import useOpenState from '../../../../hooks/useOpenState';
import {useEffect} from 'react';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import InformationBox from '../../../../components/other/informationBox';
import Typography from '@mui/material/Typography';
import {hasOneOfPermission, Permissions, user} from '../../../../store/reducers/user';
import {ctxReferralModalClosed, setReferralModalClosed} from '../../../../store/reducers/patientContext';
import Notes from '../../../../util/notes';
import API from '../../../../app/api';
import {mapReferralDataToFormStructure} from '../../../ward/forms/formsUtil';
import moment from 'moment';
import Formatter from '../../../../util/formatter';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import {setSnackbar} from '../../../../store/reducers/globals';
import {streamingTypeDict} from '../../../../store/reducers/dicts';
import _ from 'lodash';

const PatientContextReferralModal = ({patient}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [isModalOpen, openModal, closeModal] = useOpenState(false, true);

    // User Data and permissions
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);
    const canTriage = dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_MANAGE_TRIAGE]));

    const wasModalClosed = useSelector(ctxReferralModalClosed);

    const streamingType = useSelector(streamingTypeDict);
    const findService = patient?.metadata?.healthcareServiceId ? _.find(streamingType, sr => sr.healthcareService?.includes(patient?.metadata?.healthcareServiceId)) : null;

    useEffect(() => {
        if (!wasModalClosed && patient?.metadata) {
            const {snReferralId, referralAcknowledge} = patient.metadata;
            if (snReferralId && !referralAcknowledge?.acknowledge) {
                openModal();
            }
        } else {
            closeModal();
        }
    }, [patient, wasModalClosed]);

    const getReferralObject = async () => {
        try {
            const data = await API.getReferralReview(patient?.metadata?.snReferralId);
            const mappedReferralData = mapReferralDataToFormStructure(data);
            mappedReferralData.hasSR = patient?.metadata?.patientFlags?.some(item => item?.name === 'S&R' && item?.checked === true) || false;
            mappedReferralData.streamingType = findService?.__description || '-';
            mappedReferralData.lastUpdateBy = userFullName;
            mappedReferralData.lastUpdateDate = moment().toISOString();
            return mappedReferralData;
        } catch (error) {
            console.error('Error fetching referral data', error);
        }
    };

    const handleAcknowledge = () => {
        const data = {
            acknowledge: true,
            noteSave: true,
        };
        API.updatePatientInWard(patient.__id, [{
                'metadata.referralAcknowledge': data,
            }])
            .then(async res => {
                if (res) {
                    if (!patient?.metadata?.referralAcknowledge?.noteSave) {
                        const referralData = await getReferralObject();
                        const note = Notes.getReferralReviewNote(referralData);
                        dispatch(addNoteForPatient(patient?.patient?.__id, {
                            note,
                            noteType: 'referralReviewForm',
                            patientsInWardsRef: [patient.__id],
                            episodeId: patient?.admission?.consultantEpisodeId || null,
                        }, patient.__id));
                    }
                    dispatch(addNoteForPatient(patient?.patient?.__id, {
                        note: Notes.getReferralAck(),
                        noteType: 'referralAcknowledgement',
                        patientsInWardsRef: [patient.__id],
                        episodeId: patient?.admission?.consultantEpisodeId || null,
                    }, patient.__id));
                }
            })
            .catch(error => {
                console.error(error);
                dispatch(setSnackbar({type: 'error', message: 'Data not saved.'}));
            });

        dispatch(setReferralModalClosed(true));
        closeModal();
    };

    const handleClose = async (_e, reason) => {
        if (reason !== 'backdropClick') {
            if (!patient?.metadata?.referralAcknowledge?.noteSave) {
                const res = await getReferralObject();
                const note = Notes.getReferralReviewNote(res);

                const data = {
                    acknowledge: false,
                    noteSave: true,
                };

                API.updatePatientInWard(patient.__id, [{
                        'metadata.referralAcknowledge': data,
                    }])
                    .then(res => {
                        if (res) {
                            dispatch(addNoteForPatient(patient?.patient?.__id, {
                                note,
                                noteType: 'referralReviewForm',
                                patientsInWardsRef: [patient.__id],
                                episodeId: patient?.admission?.consultantEpisodeId || null,
                            }, patient.__id));
                        }
                    })
                    .catch(error => {
                        console.error(error);
                        dispatch(setSnackbar({type: 'error', message: 'Data not saved.'}));
                    });
            }
            dispatch(setReferralModalClosed(true));
            closeModal();
        }
    };

    return (
        <AgyleDialog
            open={isModalOpen}
            onClose={handleClose}
            title={t('Referral Acknowledgement')}
            fullWidth
            disableEscapeKeyDown
            closeButtonOnHeader
            width="sm"
            submitText={t('Acknowledge')}
            onSave={handleAcknowledge}
            disableSave={!canTriage}
            zIndex={1300}
        >
            <>
                <InformationBox type="info">
                    <Typography variant="body1" fontWeight={600} gutterBottom>
                        {t('This patient has received a referral from an external source - Streaming and Redirection Tool.')}
                    </Typography>

                    <Typography variant="body1">
                        {t('If appropriate, please review and acknowledge the details of this referral.')}
                    </Typography>
                </InformationBox>
            </>
        </AgyleDialog>
    );
};

export default PatientContextReferralModal;